import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { mandantenAPI } from '@/lib/api';

/* Öffentliche Landingpage der BalanceTest-Beratung (Mandant "zinzino"). V3.
   Läuft OHNE Login — verlinkbar über Flyer, QR-Code, Instagram-Bio, WhatsApp.
   /zinzino          -> ganze Seite
   /zinzino-termin   -> dieselbe Seite, springt nach dem Laden der freien
                        Termine automatisch zum Slot-Picker (Deep-Link für
                        Warmkontakte). /zinzino#termin funktioniert ebenfalls.
   Consent-Text kommt vom Server (/api/public/zinzino/config) — nichts
   hartkodiert.

   Scroll-Sprache nach dem Vorbild moderner Produktseiten:
     1. Scrub-Hero        — Headline schrumpft/blendet beim Scrollen aus
     2. Gepinnte Sequenz  — 320vh hohe Szene, der Scroll treibt die Zahl
                            von 15,0:1 auf 3,0:1 und schaltet drei Kapitel
     3. Text-Fill         — Absatz füllt sich Wort für Wort beim Scrollen
     4. Parallax          — Bilder driften leicht gegen die Scrollrichtung
     5. Gestaffelte Reveals + Sticky-CTA

   Alles läuft über EINEN rAF-gedrosselten Scroll-Loop und ausschließlich
   über transform/opacity — kein Layout-Thrashing, flüssig auf dem iPhone.
   Bei "Bewegung reduzieren" (iOS-Bedienungshilfen) zeigen alle Szenen
   sofort ihren Endzustand, die Pinning-Höhen kollabieren auf normale Höhe.

   Die Schriften (Familjen Grotesk, IBM Plex Mono, Source Sans 3) werden wie
   alle Fonts des Projekts selbst gehostet (public/fonts) — kein Google-CDN. */

const MANDANT = 'zinzino';

/* Offizielle Zinzino-Bilder (cdn.cms.zinzino.com, 15.08.2026) — lokal
   gehostet wie alle Assets des Projekts (kein Fremd-CDN, DSGVO/CSP). */
const BILDER = {
  hero: '/zinzino-hero.jpg',      // BalanceTest-Kit, Hochformat
  frauen: '/zinzino-frauen.jpg',  // Frau mit Omega-3-Kapsel
  test: '/zinzino-testkit.jpg',   // Testkit + BalanceOil in Händen
};

const UTM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign',
  'utm_content', 'utm_term', 'gclid', 'fbclid',
];

/* Erstkontakt-Herkunft: beim ersten Aufruf aus URL/Referrer gelesen und in
   sessionStorage gemerkt, damit sie eine spätere Buchung überlebt, auch wenn
   die UTM-Parameter beim Navigieren verloren gehen. */
function ladeHerkunft() {
  if (typeof window === 'undefined') return {};
  try {
    const gespeichert = sessionStorage.getItem('zz-herkunft');
    if (gespeichert) return JSON.parse(gespeichert);
  } catch { /* Safari-Privatmodus o. Ä. — dann eben ohne Speicher */ }
  const params = new URLSearchParams(window.location.search);
  const herkunft = {
    referrer: document.referrer || null,
    landingpage: window.location.pathname,
  };
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) herkunft[k] = v.slice(0, 200);
  });
  try { sessionStorage.setItem('zz-herkunft', JSON.stringify(herkunft)); } catch { /* s. o. */ }
  return herkunft;
}

const MONATE_KURZ = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const fmtLabel = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()}. ${MONATE_KURZ[d.getMonth()]}`;
};

/* ================================================================ Motion
 * Ein gemeinsamer Scroll-Loop. Komponenten registrieren einen Callback,
 * der pro Frame (rAF, nur bei tatsächlichem Scroll/Resize) läuft.
 * ======================================================================= */

const scrollAbos = new Set();
let loopAktiv = false;

function loopTick() {
  scrollAbos.forEach((cb) => cb());
  loopAktiv = false;
}
function loopAnstossen() {
  if (!loopAktiv) {
    loopAktiv = true;
    requestAnimationFrame(loopTick);
  }
}

function useScrollLoop(callback, aktiv = true) {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  useEffect(() => {
    if (!aktiv) return;
    const cb = () => cbRef.current();
    scrollAbos.add(cb);
    window.addEventListener('scroll', loopAnstossen, { passive: true });
    window.addEventListener('resize', loopAnstossen);
    loopAnstossen();
    return () => {
      scrollAbos.delete(cb);
      if (scrollAbos.size === 0) {
        window.removeEventListener('scroll', loopAnstossen);
        window.removeEventListener('resize', loopAnstossen);
      }
    };
  }, [aktiv]);
}

function useReduzierteBewegung() {
  const [reduziert, setReduziert] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduziert(mq.matches);
    const h = (e) => setReduziert(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return reduziert;
}

/** Fortschritt 0..1 einer gepinnten Szene (Container hoch, Kind sticky). */
function useSzenenScrub(ref, reduziert) {
  const [p, setP] = useState(reduziert ? 1 : 0);
  useScrollLoop(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spielraum = el.offsetHeight - window.innerHeight;
    if (spielraum <= 0) return;
    setP(Math.max(0, Math.min(1, -r.top / spielraum)));
  }, !reduziert);
  useEffect(() => { if (reduziert) setP(1); }, [reduziert]);
  return p;
}

/** Einblenden bei Sichtbarkeit (IntersectionObserver, einmalig). */
function useReveal(reduziert) {
  useEffect(() => {
    const ziele = document.querySelectorAll('[data-reveal]');
    if (reduziert) {
      ziele.forEach((el) => el.classList.add('zz-sichtbar'));
      return;
    }
    const obs = new IntersectionObserver(
      (eintraege) => {
        eintraege.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('zz-sichtbar');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    ziele.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });
}

/** Zählt hoch, sobald das Element sichtbar wird. */
function useHochzaehlen(ziel, dauer = 1600, reduziert = false) {
  const ref = useRef(null);
  const [wert, setWert] = useState(0);
  useEffect(() => {
    if (reduziert) { setWert(ziel); return; }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((e) => {
      if (!e[0].isIntersecting) return;
      obs.disconnect();
      const start = performance.now();
      const schritt = (t) => {
        const p = Math.min(1, (t - start) / dauer);
        const eased = 1 - Math.pow(1 - p, 3);
        setWert(Math.round(ziel * eased));
        if (p < 1) requestAnimationFrame(schritt);
      };
      requestAnimationFrame(schritt);
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ziel, dauer, reduziert]);
  return [ref, wert];
}

/* ---------------------------------------------------- Parallax-Bild */

function ParallaxBild({ quelle, alt = '', staerke = 0.1, klasse = '' }) {
  const huelle = useRef(null);
  const inner = useRef(null);
  const reduziert = useReduzierteBewegung();
  useScrollLoop(() => {
    const h = huelle.current, i = inner.current;
    if (!h || !i) return;
    const r = h.getBoundingClientRect();
    const versatz = (r.top + r.height / 2 - window.innerHeight / 2) * -staerke;
    i.style.transform = `translate3d(0, ${versatz.toFixed(1)}px, 0) scale(1.12)`;
  }, !reduziert);
  return (
    <div className={`zz-parallax ${klasse}`} ref={huelle}>
      <div className="zz-parallax-inner" ref={inner}>
        {quelle
          ? <img className="zz-bild" src={quelle} alt={alt} />
          : <div className="zz-bild zz-bild-platzhalter" aria-hidden="true" />}
      </div>
    </div>
  );
}

/* -------------------------------------------- Text-Fill (Wort für Wort) */

function TextFill({ text, klasse = '' }) {
  const ref = useRef(null);
  const reduziert = useReduzierteBewegung();
  const woerter = useMemo(() => text.split(' '), [text]);
  const [anzahl, setAnzahl] = useState(reduziert ? woerter.length : 0);

  useScrollLoop(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    // füllt sich, während der Absatz von 88 % auf 38 % der Viewporthöhe wandert
    const p = Math.max(0, Math.min(1, (vh * 0.88 - r.top) / (vh * 0.5)));
    setAnzahl(Math.round(p * woerter.length));
  }, !reduziert);
  useEffect(() => { if (reduziert) setAnzahl(woerter.length); }, [reduziert, woerter.length]);

  return (
    <p className={`zz-textfill ${klasse}`} ref={ref} aria-label={text}>
      {woerter.map((w, i) => (
        <span key={i} aria-hidden="true"
              className={i < anzahl ? 'zz-wort zz-wort-an' : 'zz-wort'}>
          {w}{' '}
        </span>
      ))}
    </p>
  );
}

/* ------------------------------------------- Gepinnte Sequenz (Kapitel) */

const KAPITEL = [
  {
    nr: '01',
    titel: 'Testen',
    text: 'Vier Tropfen Blut aus der Fingerkuppe. Zwei Minuten. Das unabhängige Labor Vitas in Oslo misst elf Fettsäuren und sechs Indikatoren — darunter dein Omega-6:3-Verhältnis.',
  },
  {
    nr: '02',
    titel: 'Anpassen',
    text: 'Mit dem Wert vor Augen entscheidest du über Ernährung und Supplementierung — nach Zahl, nicht nach Gefühl.',
  },
  {
    nr: '03',
    titel: 'Nachtesten',
    text: 'Nach 120 Tagen der zweite Test. Erst der Vergleich zeigt schwarz auf weiß, ob sich etwas bewegt hat.',
  },
];

function GepinnteSequenz({ reduziert }) {
  const ref = useRef(null);
  const p = useSzenenScrub(ref, reduziert);

  // Zahl läuft im ersten Drittel nicht, dann von 15,0 auf 3,0
  const zahlP = Math.max(0, Math.min(1, (p - 0.12) / 0.76));
  const eased = 1 - Math.pow(1 - zahlP, 2);
  const verhaeltnis = (15 - eased * 12).toFixed(1).replace('.', ',');
  const imZiel = eased > 0.9;
  const bandLinks = 86 - eased * 68;

  // aktives Kapitel
  const idx = Math.min(KAPITEL.length - 1, Math.floor(p * KAPITEL.length));

  return (
    <section
      className={reduziert ? 'zz-szene zz-szene-flach' : 'zz-szene'}
      ref={ref}
      aria-label="Ablauf: testen, anpassen, nachtesten"
    >
      <div className="zz-szene-sticky">
        <p className="zz-eyebrow zz-eyebrow-hell">Das Prinzip</p>

        <div className={imZiel ? 'zz-szene-zahl zz-szene-zahl-ziel' : 'zz-szene-zahl'}>
          {verhaeltnis}
          <span className="zz-szene-einheit">:1</span>
        </div>

        <div className="zz-band" aria-hidden="true">
          <div className="zz-band-zone" />
          <div className="zz-band-linie" />
          <div className="zz-band-marke" style={{ left: `${bandLinks}%` }} />
          <div className="zz-band-achse">
            <span>1:1</span>
            <span className="zz-band-ziel">Zielbereich unter 3:1</span>
            <span>20:1</span>
          </div>
        </div>

        {reduziert ? (
          <div className="zz-kapitel-alle">
            {KAPITEL.map((k) => (
              <div key={k.nr} className="zz-kapitel zz-kapitel-an">
                <span className="zz-nr">{k.nr}</span>
                <h3>{k.titel}</h3>
                <p>{k.text}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="zz-kapitel-buehne">
            {KAPITEL.map((k, i) => (
              <div key={k.nr}
                   className={i === idx ? 'zz-kapitel zz-kapitel-an' : 'zz-kapitel'}
                   aria-hidden={i !== idx}>
                <span className="zz-nr">{k.nr}</span>
                <h3>{k.titel}</h3>
                <p>{k.text}</p>
              </div>
            ))}
          </div>
        )}

        {!reduziert && (
          <div className="zz-szene-punkte" aria-hidden="true">
            {KAPITEL.map((k, i) => (
              <span key={k.nr}
                    className={i === idx ? 'zz-punktchen zz-punktchen-an' : 'zz-punktchen'} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Kleinkram */

function Faq({ frage, antwort }) {
  const [offen, setOffen] = useState(false);
  return (
    <div className={offen ? 'zz-faq zz-faq-offen' : 'zz-faq'}>
      <button aria-expanded={offen} onClick={() => setOffen(!offen)}>
        <span>{frage}</span>
        <span className="zz-faq-zeichen">{offen ? '−' : '+'}</span>
      </button>
      {offen && <p>{antwort}</p>}
    </div>
  );
}

function Feld({ id, label, wert, onChange, fehler, typ = 'text', autoComplete }) {
  return (
    <div className="zz-feld" data-fehler={!!fehler}>
      <label htmlFor={id}>{label}</label>
      <input id={id} type={typ} value={wert} onChange={onChange}
             autoComplete={autoComplete} aria-invalid={!!fehler} />
      {fehler && <span className="zz-fehler">{fehler}</span>}
    </div>
  );
}

/* ------------------------------------------------------------ Hauptseite */

export default function ZinzinoLanding() {
  const location = useLocation();
  const reduziert = useReduzierteBewegung();
  useReveal(reduziert);

  // Scrub-Hero: schrumpft und blendet beim Scrollen aus
  const heroRef = useRef(null);
  const heroInhaltRef = useRef(null);
  const [ctaSichtbar, setCtaSichtbar] = useState(false);
  useScrollLoop(() => {
    const hero = heroRef.current, inhalt = heroInhaltRef.current;
    if (!hero || !inhalt) return;
    const h = hero.offsetHeight || 1;
    const p = Math.max(0, Math.min(1, window.scrollY / (h * 0.85)));
    inhalt.style.transform =
      `translate3d(0, ${(p * 44).toFixed(1)}px, 0) scale(${(1 - p * 0.07).toFixed(3)})`;
    inhalt.style.opacity = String(1 - p * 0.75);
    setCtaSichtbar(window.scrollY > h * 0.9);
  }, !reduziert);
  useEffect(() => {
    if (!reduziert) return;
    const onScroll = () =>
      setCtaSichtbar(window.scrollY > (heroRef.current?.offsetHeight || 600));
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [reduziert]);

  // Fortschrittsbalken
  const balkenRef = useRef(null);
  useScrollLoop(() => {
    const el = balkenRef.current;
    if (!el) return;
    const max = document.body.scrollHeight - window.innerHeight;
    el.style.transform = `scaleX(${max > 0 ? Math.min(1, window.scrollY / max) : 0})`;
  }, true);

  // Buchung
  const [config, setConfig] = useState(null);
  const [tage, setTage] = useState([]);
  const [ladeSlots, setLadeSlots] = useState(true);
  const [tagIndex, setTagIndex] = useState(0);
  const [slot, setSlot] = useState(null);          // gewählter Slot (slot_start)
  const [form, setForm] = useState({
    vorname: '', nachname: '', telefon: '', email: '',
    nachricht: '', consent: false, website: '',
  });
  const [fehler, setFehler] = useState({});
  const [sende, setSende] = useState(false);
  const [bestaetigung, setBestaetigung] = useState(null);
  const [serverFehler, setServerFehler] = useState('');
  const herkunft = useMemo(ladeHerkunft, []);
  const buchungRef = useRef(null);
  const [testeRef, testeWert] = useHochzaehlen(1771355, 1900, reduziert);

  const dauerMin = config?.termin_dauer_min || 20;
  const kontaktEmail = config?.kontakt_email || 'info@p1-pforzheim.de';

  useEffect(() => {
    const vorher = document.title;
    document.title = 'BalanceTest-Beratung — Testen. Nicht raten.';
    return () => { document.title = vorher; };
  }, []);

  const slotsLaden = useCallback(async () => {
    setLadeSlots(true);
    try {
      const [c, s] = await Promise.all([
        mandantenAPI.publicConfig(MANDANT),
        mandantenAPI.publicSlots(MANDANT),
      ]);
      setConfig(c.data);
      setTage(s.data.tage || []);
    } catch { /* Seite bleibt nutzbar, Kontakt steht unten */ }
    setLadeSlots(false);
  }, []);
  useEffect(() => { slotsLaden(); }, [slotsLaden]);

  // Deep-Link: /zinzino-termin (oder #termin) springt direkt zum Slot-Picker
  useEffect(() => {
    if (ladeSlots) return;
    const direkt = location.pathname === '/zinzino-termin' || location.hash === '#termin';
    if (direkt) {
      // Kein smooth: beim Seitenaufruf soll der Picker SOFORT stehen (smooth
      // bricht zudem ab, wenn der Tab im Hintergrund lädt, z. B. aus WhatsApp)
      setTimeout(() => buchungRef.current?.scrollIntoView({ block: 'start' }), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ladeSlots]);

  const setzen = (feld) => (e) => {
    const wert = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [feld]: wert }));
    setFehler((f) => ({ ...f, [feld]: undefined }));
  };

  function pruefen() {
    const f = {};
    if (!slot) f.slot = 'Bitte zuerst einen Termin auswählen.';
    if (form.vorname.trim().length < 2) f.vorname = 'Bitte Vorname eintragen.';
    if (form.nachname.trim().length < 2) f.nachname = 'Bitte Name eintragen.';
    if ((form.telefon.match(/\d/g) || []).length < 6)
      f.telefon = 'Bitte eine erreichbare Nummer eintragen.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email))
      f.email = 'Diese E-Mail-Adresse stimmt so nicht.';
    if (!form.consent) f.consent = 'Ohne Einwilligung dürfen wir den Termin nicht speichern.';
    setFehler(f);
    return Object.keys(f).length === 0;
  }

  async function buchen() {
    setServerFehler('');
    if (!pruefen()) {
      buchungRef.current
        ?.querySelector("[data-fehler='true']")
        ?.scrollIntoView({ block: 'center', behavior: reduziert ? 'auto' : 'smooth' });
      return;
    }
    setSende(true);
    try {
      const res = await mandantenAPI.publicBuchen(MANDANT, {
        slot_start: slot.slot_start,
        vorname: form.vorname.trim(), nachname: form.nachname.trim(),
        telefon: form.telefon.trim(), email: form.email.trim(),
        nachricht: form.nachricht.trim(),
        consent: true, website: form.website,
        herkunft,
      });
      setBestaetigung({ ...res.data, wochentag: slot.wochentag });
    } catch (e) {
      if (e?.response?.status === 409) {
        setServerFehler(e?.response?.data?.detail
          || 'Der Termin wurde gerade vergeben. Bitte einen anderen wählen.');
        setSlot(null);
        slotsLaden();
      } else {
        setServerFehler(e?.response?.data?.detail
          || `Die Buchung kam nicht durch. Bitte noch einmal versuchen oder kurz eine Mail an ${kontaktEmail}.`);
      }
    } finally {
      setSende(false);
    }
  }

  const tag = tage[tagIndex];

  return (
    <div className="zz-root">
      <style>{CSS}</style>

      <div className="zz-fortschritt" ref={balkenRef} />

      <header className="zz-kopf">
        <span className="zz-marke">BalanceTest</span>
        <span className="zz-kopf-zusatz">
          betreut von {config?.berater || 'Elvan Ibar'} · P1 Performance Center
        </span>
      </header>

      <main>
        {/* -------------------------------------------------- Scrub-Hero */}
        <section className="zz-hero" ref={heroRef}>
          <div className="zz-hero-inhalt" ref={heroInhaltRef}>
            <p className="zz-eyebrow">Omega-6 zu Omega-3 · Bluttest</p>
            <h1 className="zz-h1">
              Testen<span className="zz-punkt">.</span>
              <br />Nicht raten<span className="zz-punkt">.</span>
            </h1>
            <p className="zz-lead">
              Vier Tropfen Blut zeigen, wie deine Fettsäuren tatsächlich stehen.
              Danach entscheidest du auf Basis eines Werts — nicht auf Basis
              einer Werbeaussage.
            </p>
            <a className="zz-cta" href="#termin">Termin auswählen</a>
            <p className="zz-hero-fuss">
              {dauerMin} Minuten · im P1 Performance Center Pforzheim · kostenfrei
            </p>
          </div>
          <div className="zz-hero-bild">
            <ParallaxBild quelle={BILDER.hero} alt="Zinzino BalanceTest-Kit" staerke={0.06} klasse="zz-parallax-hoch" />
          </div>
          {!reduziert && (
            <div className="zz-scrollwink" aria-hidden="true">
              <span />
            </div>
          )}
        </section>

        {/* --------------------------------- Gepinnte Sequenz (Kernstück) */}
        <GepinnteSequenz reduziert={reduziert} />

        {/* -------------------------------------------------------- Zahlen */}
        <section className="zz-zahlen" ref={testeRef}>
          <div data-reveal>
            <span className="zz-zahl">{testeWert.toLocaleString('de-DE')}</span>
            <span className="zz-zahl-label">BalanceTests weltweit durchgeführt</span>
          </div>
          <div data-reveal>
            <span className="zz-zahl">97 %</span>
            <span className="zz-zahl-label">der Erstgetesteten sind nicht in Balance (Ziel: unter 3:1)</span>
          </div>
          <div data-reveal>
            <span className="zz-zahl">120</span>
            <span className="zz-zahl-label">Tage bis zum Nachtest — so lange brauchen die Blutzellen, um sich zu erneuern</span>
          </div>
        </section>

        {/* ------------------------------------------------- Warum · Frauen */}
        <section className="zz-warum">
          <div className="zz-warum-kopf">
            <p className="zz-eyebrow zz-eyebrow-hell" data-reveal>Warum das Thema</p>
            <h2 className="zz-h2" data-reveal>
              Für Frauen ist der Wert selten nebensächlich
            </h2>
            <TextFill
              klasse="zz-abschnitt-lead"
              text="Omega-3 ist kein Wellness-Zusatz, sondern ein Baustoff — für Zellmembranen, Nervengewebe, Netzhaut. Der Bedarf verschiebt sich über das Leben, und in mehreren Phasen betrifft das Frauen anders als Männer."
            />
          </div>

          <div className="zz-karten">
            <article data-reveal>
              <span className="zz-kartenr">01</span>
              <h3>Kinderwunsch, Schwangerschaft, Stillzeit</h3>
              <p>
                DHA trägt zur normalen Entwicklung von Gehirn und Augen beim
                Fötus und beim gestillten Säugling bei.<sup>1</sup> Der Körper
                gibt in dieser Zeit ab — was fehlt, holt er sich aus den
                eigenen Reserven.
              </p>
            </article>
            <article data-reveal>
              <span className="zz-kartenr">02</span>
              <h3>Herz, Kopf, Augen</h3>
              <p>
                EPA und DHA tragen zu einer normalen Herzfunktion bei.<sup>2</sup>{' '}
                DHA trägt zur Erhaltung normaler Gehirnfunktion und normaler
                Sehkraft bei.<sup>3</sup> Alles Systeme, die man erst bemerkt,
                wenn sie nachlassen.
              </p>
            </article>
            <article data-reveal>
              <span className="zz-kartenr">03</span>
              <h3>Der Alltag zieht in die andere Richtung</h3>
              <p>
                Sonnenblumen- und Distelöl, Fertigprodukte, Nüsse, Geflügel:
                Omega-6 ist überall. Fisch zweimal die Woche schafft kaum
                jemand. Dieses Ungleichgewicht baut sich über Monate auf,
                lautlos.
              </p>
            </article>
            <article data-reveal>
              <span className="zz-kartenr">04</span>
              <h3>Wer trainiert, misst sonst alles</h3>
              <p>
                Gewicht, Schritte, Schlaf, Zyklus. Der eigene Fettsäurestatus
                ist die eine Zahl, die fast niemand kennt — obwohl sie zu den
                wenigen gehört, die man selbst verändern kann.
              </p>
            </article>
          </div>

          <ParallaxBild quelle={BILDER.frauen} alt="Frau hält eine Omega-3-Kapsel" staerke={0.1} klasse="zz-warum-bild" />

          <p className="zz-fussnoten" data-reveal>
            <sup>1</sup> Die mütterliche Aufnahme von DHA trägt zur normalen
            Entwicklung von Gehirn und Augen beim Fötus und beim gestillten
            Säugling bei. Die positive Wirkung stellt sich bei einer zusätzlichen
            Aufnahme von 200 mg DHA pro Tag zusätzlich zur empfohlenen
            Tagesdosis von 250 mg EPA und DHA ein.{' '}
            <sup>2</sup> Bei einer täglichen Aufnahme von 250 mg EPA und DHA.{' '}
            <sup>3</sup> Bei einer täglichen Aufnahme von 250 mg DHA.
          </p>
        </section>

        {/* --------------------------------------------------- Testkit-Bild */}
        <section className="zz-kit">
          <ParallaxBild quelle={BILDER.test} alt="BalanceTest-Karte und BalanceOil-Flasche" staerke={0.08} />
          <div className="zz-kit-text" data-reveal>
            <h2 className="zz-h2">Der Test kommt aus der Box, das Ergebnis aus Norwegen</h2>
            <p>
              Ausgewertet wird bei Vitas in Oslo — einem unabhängigen,
              GMP-zertifizierten Labor mit über 25 Jahren Fettsäureanalyse,
              hervorgegangen aus der Universität Oslo. Nach 10 bis 21 Tagen
              bekommst du deinen persönlichen Bericht: elf Fettsäuren, sechs
              Indikatoren, dein Ausgangspunkt.
            </p>
            <p>
              Durch Beratung, Test und Auswertung führt dich Elvan Ibar,
              Headcoach für Frauentrainings im P1 Performance Center — von der
              ersten Frage bis zum Nachtest, persönlich und ohne Verkaufsdruck.
            </p>
          </div>
        </section>

        {/* --------------------------------------- Nach dem Test (Betreuung) */}
        <section className="zz-danach" aria-label="Betreuung nach dem Test">
          <div className="zz-danach-inner">
            <p className="zz-eyebrow" data-reveal>Nach dem Test</p>
            <h2 className="zz-h2" data-reveal>Gemessen ist erst der Anfang</h2>
            <TextFill
              klasse="zz-abschnitt-lead"
              text="Ein Wert allein ändert nichts. Deshalb endet die Begleitung nicht mit der Testkarte — sie fängt dort an."
            />
            <div className="zz-danach-schritte">
              <article data-reveal>
                <span className="zz-kartenr">Tag 0</span>
                <h3>Beratung &amp; Test</h3>
                <p>{dauerMin} Minuten im P1 — Elvan nimmt den Test gemeinsam mit dir ab, wenn du dich dafür entscheidest.</p>
              </article>
              <article data-reveal>
                <span className="zz-kartenr">Tag 10–21</span>
                <h3>Auswertung — zusammen</h3>
                <p>Dein Bericht kommt aus Oslo, anonym ausgewertet über deine Test-ID. Elvan geht ihn mit dir durch, Wert für Wert.</p>
              </article>
              <article data-reveal>
                <span className="zz-kartenr">Tag 30 · 60</span>
                <h3>Check-ins</h3>
                <p>Kurze Rückfragen statt Funkstille: Wie klappt die Routine, ist die Lieferung da, gibt es Fragen?</p>
              </article>
              <article data-reveal>
                <span className="zz-kartenr">Tag 120</span>
                <h3>Nachtest</h3>
                <p>Der zweite Test zeigt schwarz auf weiß, was sich verändert hat — dein Vorher-nachher in einer Zahl.</p>
              </article>
            </div>
            <p className="zz-marke-zeile" data-reveal>
              Hinter Test und Produkten steht Zinzino, ein skandinavisches
              Unternehmen, gegründet 2005 — mit einem Versprechen: die Welt
              wieder ins Gleichgewicht bringen, eine Person nach der anderen.
              Im BalanceOil+ stecken rund 60 % Fischöl aus kleinen, wild
              gefangenen Fischen und 40 % extra natives Olivenöl, dessen
              Polyphenole die empfindlichen Omega-3-Fettsäuren vor Oxidation
              schützen sollen.
            </p>
          </div>
        </section>

        {/* -------------------------------------------------- Terminbuchung */}
        <section className="zz-termin" id="termin" ref={buchungRef}>
          {bestaetigung ? (
            <div className="zz-bestaetigung">
              <span className="zz-haken" aria-hidden="true">✓</span>
              <h2>Termin steht.</h2>
              <p className="zz-bestaetigung-termin">
                {bestaetigung.wochentag}, {fmtLabel(bestaetigung.datum)} um{' '}
                {bestaetigung.uhrzeit} Uhr
              </p>
              <p className="zz-bestaetigung-ort">
                bis {bestaetigung.ende} Uhr · {bestaetigung.ort}
              </p>
              <p>
                Du bekommst vorher eine Erinnerung. Wenn etwas dazwischenkommt,
                melde dich einfach kurz.
              </p>
              <a className="zz-cta zz-cta-hell"
                 href={mandantenAPI.icsUrl(MANDANT, bestaetigung.id)}>
                In den Kalender eintragen
              </a>
            </div>
          ) : (
            <>
              <div className="zz-termin-kopf" data-reveal>
                <p className="zz-eyebrow zz-eyebrow-hell">Termin</p>
                <h2 className="zz-h2">Wann passt es dir?</h2>
                <p className="zz-abschnitt-lead">
                  {dauerMin} Minuten, kostenfrei, ohne Kaufverpflichtung.
                  {config?.ort ? ` ${config.ort}.` : ''}
                </p>
                <div className="zz-beraterin">
                  <img src="/elvan.jpg" alt="Elvan Ibar" className="zz-beraterin-foto" />
                  <div>
                    <p className="zz-beraterin-name">Elvan Ibar</p>
                    <p className="zz-beraterin-rolle">Deine Beraterin · Headcoach Frauentraining im P1</p>
                  </div>
                </div>
              </div>

              {ladeSlots && <p className="zz-hinweis">Freie Termine werden geladen …</p>}

              {!ladeSlots && tage.length === 0 && (
                <p className="zz-hinweis">
                  Gerade ist nichts frei. Schreib uns kurz an{' '}
                  <a href={`mailto:${kontaktEmail}`}>{kontaktEmail}</a>,
                  dann finden wir einen Termin.
                </p>
              )}

              {!ladeSlots && tage.length > 0 && (
                <>
                  <div className="zz-tage" role="tablist" aria-label="Tag wählen">
                    {tage.map((t, i) => (
                      <button key={t.datum} role="tab" aria-selected={i === tagIndex}
                              className={i === tagIndex ? 'zz-tag zz-tag-aktiv' : 'zz-tag'}
                              onClick={() => { setTagIndex(i); setSlot(null); }}>
                        <span className="zz-tag-wochentag">{t.wochentag.slice(0, 2)}</span>
                        <span className="zz-tag-datum">{fmtLabel(t.datum)}</span>
                        <span className="zz-tag-anzahl">{t.slots.length} frei</span>
                      </button>
                    ))}
                  </div>

                  <div className="zz-slots" data-fehler={!!fehler.slot}>
                    {tag?.slots.map((s) => (
                      <button key={s.slot_start}
                              className={slot?.slot_start === s.slot_start ? 'zz-slot zz-slot-aktiv' : 'zz-slot'}
                              onClick={() => {
                                setSlot({ ...s, wochentag: tag.wochentag, datum: tag.datum });
                                setFehler((f) => ({ ...f, slot: undefined }));
                              }}>
                        {s.uhrzeit}
                      </button>
                    ))}
                  </div>
                  {fehler.slot && <span className="zz-fehler">{fehler.slot}</span>}

                  <div className="zz-formular">
                    <div className="zz-grid">
                      <Feld id="vorname" label="Vorname" wert={form.vorname}
                            onChange={setzen('vorname')} fehler={fehler.vorname}
                            autoComplete="given-name" />
                      <Feld id="nachname" label="Name" wert={form.nachname}
                            onChange={setzen('nachname')} fehler={fehler.nachname}
                            autoComplete="family-name" />
                    </div>
                    <div className="zz-grid">
                      <Feld id="telefon" label="Telefon" typ="tel" wert={form.telefon}
                            onChange={setzen('telefon')} fehler={fehler.telefon}
                            autoComplete="tel" />
                      <Feld id="email" label="E-Mail" typ="email" wert={form.email}
                            onChange={setzen('email')} fehler={fehler.email}
                            autoComplete="email" />
                    </div>

                    <div className="zz-feld">
                      <label htmlFor="nachricht">
                        Etwas, das ich vorher wissen sollte? (optional)
                      </label>
                      <textarea id="nachricht" rows={2} value={form.nachricht}
                                onChange={setzen('nachricht')} maxLength={1000} />
                    </div>

                    {/* Honeypot — für Menschen unsichtbar, Bots füllen es aus */}
                    <div className="zz-hp" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input id="website" tabIndex={-1} autoComplete="off"
                             value={form.website} onChange={setzen('website')} />
                    </div>

                    <div className="zz-consent" data-fehler={!!fehler.consent}>
                      <input id="consent" type="checkbox" checked={form.consent}
                             onChange={setzen('consent')} aria-invalid={!!fehler.consent} />
                      <label htmlFor="consent">
                        {config?.consent_text || 'Einwilligung zur Datenverarbeitung.'}{' '}
                        <a href="/datenschutz">Datenschutz</a>
                      </label>
                    </div>
                    {fehler.consent && <span className="zz-fehler">{fehler.consent}</span>}

                    {serverFehler && <p className="zz-serverfehler">{serverFehler}</p>}

                    <button className="zz-cta zz-cta-block" onClick={buchen} disabled={sende}>
                      {sende ? 'Wird gebucht …' : 'Termin verbindlich buchen'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </section>

        {/* ----------------------------------------------------------- FAQ */}
        <section className="zz-faqs">
          <h2 className="zz-h2" data-reveal>Häufige Fragen</h2>
          <div data-reveal>
            <Faq frage="Kostet der Termin etwas?"
                 antwort="Nein. Der Beratungstermin ist kostenfrei und unverbindlich. Der Test selbst ist ein Produkt und kostet extra — den Preis besprechen wir vor Ort, bevor du dich entscheidest." />
            <Faq frage="Muss ich danach etwas kaufen?"
                 antwort="Nein. Es kommt regelmäßig vor, dass ein Test nicht der richtige nächste Schritt ist. Dann sage ich das." />
            <Faq frage="Tut die Blutentnahme weh?"
                 antwort="Ein kurzer Piks in die Fingerkuppe, wie beim Blutzuckermessen. Die vier Tropfen sind in unter zwei Minuten auf der Karte." />
            <Faq frage="Was wird eigentlich gemessen?"
                 antwort="Elf Fettsäuren aus deinem Blut und daraus sechs Indikatoren — unter anderem dein Omega-6:3-Verhältnis, der Omega-3-Index und der Arachidonsäure-Index. Alles steht farbcodiert und erklärt in deinem persönlichen Bericht." />
            <Faq frage="Wie lange dauert das Ergebnis?"
                 antwort="10 bis 21 Tage nach dem Einsenden. Ausgewertet wird bei Vitas in Oslo, einem unabhängigen und GMP-zertifizierten Labor — du wirst per SMS oder E-Mail benachrichtigt." />
            <Faq frage="Ich bin schwanger oder stille. Geht das?"
                 antwort="Der Test selbst ist eine reine Messung. Ob und welche Nahrungsergänzung für dich sinnvoll ist, besprichst du bitte zusätzlich mit deiner Ärztin oder Hebamme — das gehört in ärztliche Hand, nicht in meine." />
            <Faq frage="Gibt es das auch vegan oder für Kinder?"
                 antwort="Ja. BalanceOil+ Vegan basiert auf marinem Mikroalgenöl statt Fischöl, für Kinder gibt es BalanceOil Tutti Frutti mit fruchtigem Geschmack und angepasster Dosierung — und AquaX lässt sich einfach in Wasser einrühren. Was zu dir passt, klären wir in der Beratung." />
            <Faq frage="Muss ich ein Abo abschließen?"
                 antwort="Nein — ein Abo ist freiwillig und nie Voraussetzung. Wenn es für dich passt: Nachfüllpackungen kommen alle zwei Monate, und bevor du irgendetwas bestellst, erkläre ich dir transparent Laufzeit, Lieferintervall, Versandkosten und wie du kündigst oder änderst." />
            <Faq frage="Bleibt mein Ergebnis privat?"
                 antwort="Ja. Die Testkarte geht nur mit einer Test-ID ins Labor und wird anonym ausgewertet — dein Ergebnis rufst du selbst ab. Ich sehe deine Werte nur, wenn du sie in der Auswertung mit mir teilen möchtest." />
          </div>
        </section>
      </main>

      <footer className="zz-fuss">
        <p className="zz-disclaimer">
          Nahrungsergänzungsmittel sind kein Ersatz für eine ausgewogene und
          abwechslungsreiche Ernährung. Die Angaben auf dieser Seite dienen der
          Information und sind nicht zur Diagnose, Behandlung oder Heilung von
          Krankheiten bestimmt. Bei gesundheitlichen Beschwerden wende dich bitte
          an eine Ärztin oder einen Arzt.
        </p>
        <p>
          Verantwortlich für die Datenverarbeitung:{' '}
          {config?.verantwortlicher || 'Personal Training Lounge, Inh. Philip Nguyen'} ·{' '}
          <a href="/impressum">Impressum</a> · <a href="/datenschutz">Datenschutz</a>
        </p>
        <p>
          Fragen oder Termin verschieben:{' '}
          <a href={`mailto:${kontaktEmail}`}>{kontaktEmail}</a> · Technische
          Verarbeitung im Auftrag: {config?.verarbeiter || 'P1 Performance Center GmbH'}
        </p>
      </footer>

      {!bestaetigung && (
        <a className={ctaSichtbar ? 'zz-sticky zz-sticky-an' : 'zz-sticky'} href="#termin">
          Termin auswählen
        </a>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Styles */

const CSS = `
.zz-root {
  --aubergine: #4A3423;   /* Cognac-Dunkelbraun (hist. Name) */
  --tief: #2C1E13;        /* Espresso */
  --creme: #F4ECDF;       /* Sand */
  --gold: #BE7A3D;        /* Cognac-Akzent (hist. Name) */
  --balance: #4CA07B;
  --signal: #C4543A;
  --linie: rgba(74,52,35,.16);
  --linie-hell: rgba(244,236,223,.22);
  --ease: cubic-bezier(.22,.9,.28,1);
  background: var(--creme);
  color: var(--aubergine);
  font-family: "Source Sans 3", system-ui, sans-serif;
  font-size: 17px; line-height: 1.6;
  /* clip statt hidden: hidden macht das Element zum Scroll-Container und
     bricht position:sticky der gepinnten Szene — clip schneidet nur ab */
  overflow-x: hidden; overflow-x: clip;
}
.zz-root *, .zz-root *::before, .zz-root *::after { box-sizing: border-box; }
.zz-root h1, .zz-root h2, .zz-root h3 {
  font-family: "Familjen Grotesk", "Source Sans 3", sans-serif;
  font-weight: 700; letter-spacing: -.025em; margin: 0;
}
.zz-root a { color: inherit; }
.zz-root :focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; }

.zz-fortschritt {
  position: fixed; top: 0; left: 0; right: 0; height: 3px; z-index: 60;
  background: var(--gold); transform-origin: 0 50%; transform: scaleX(0);
  will-change: transform;
}

.zz-kopf {
  display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;
  padding: 18px 22px; background: var(--tief); color: var(--creme);
}
.zz-marke { font-family: "Familjen Grotesk", sans-serif; font-weight: 700; font-size: 19px; letter-spacing: -.03em; }
.zz-kopf-zusatz { font-size: 14px; opacity: .7; }

/* ---- Scrub-Hero */
.zz-hero {
  background: var(--tief); color: var(--creme);
  min-height: 92vh; padding: 52px 22px 70px;
  position: relative; overflow: hidden;
  display: flex; align-items: center;
}
.zz-hero-inhalt {
  max-width: 720px; margin: 0 auto; position: relative; z-index: 2;
  will-change: transform, opacity; transform-origin: 50% 20%;
}
.zz-eyebrow {
  font-family: "IBM Plex Mono", monospace; font-size: 11.5px;
  letter-spacing: .16em; text-transform: uppercase; opacity: .62; margin: 0 0 24px;
}
.zz-eyebrow-hell { opacity: .55; }
.zz-h1 { font-size: clamp(46px, 13vw, 92px); line-height: .94; margin-bottom: 22px; }
.zz-punkt { color: var(--gold); }
.zz-lead { font-size: 18px; max-width: 42ch; opacity: .85; margin: 0 0 34px; }
.zz-hero-fuss { font-family: "IBM Plex Mono", monospace; font-size: 12.5px; opacity: .6; margin: 16px 0 0; }
.zz-hero-bild { display: none; }

.zz-scrollwink {
  position: absolute; bottom: 22px; left: 50%; transform: translateX(-50%);
  width: 24px; height: 38px; border: 1.5px solid var(--linie-hell); border-radius: 14px;
}
.zz-scrollwink span {
  position: absolute; top: 7px; left: 50%; width: 3px; height: 8px;
  margin-left: -1.5px; border-radius: 2px; background: var(--gold);
  animation: zz-wink 1.8s var(--ease) infinite;
}
@keyframes zz-wink {
  0% { transform: translateY(0); opacity: 1; }
  70% { transform: translateY(12px); opacity: 0; }
  100% { transform: translateY(0); opacity: 0; }
}

/* ---- CTA */
.zz-cta {
  display: inline-block; background: var(--gold); color: var(--tief);
  font-family: "Familjen Grotesk", sans-serif; font-weight: 600; font-size: 17px;
  text-decoration: none; padding: 16px 32px; border: none; cursor: pointer;
  border-radius: 999px; transition: transform .18s ease, filter .18s ease;
}
.zz-cta:hover { filter: brightness(1.07); transform: translateY(-1px); }
.zz-cta:disabled { opacity: .5; cursor: default; transform: none; }
.zz-cta-block { display: block; width: 100%; margin-top: 26px; }
.zz-cta-hell { background: var(--creme); color: var(--aubergine); margin-top: 24px; }

/* ---- Gepinnte Sequenz */
.zz-szene { height: 320vh; background: var(--aubergine); color: var(--creme); position: relative; }
.zz-szene-flach { height: auto; }
.zz-szene-sticky {
  position: sticky; top: 0; height: 100vh;
  display: flex; flex-direction: column; justify-content: center;
  padding: 40px 22px; max-width: 720px; margin: 0 auto;
}
.zz-szene-flach .zz-szene-sticky { position: static; height: auto; padding: 56px 22px; }
.zz-szene-zahl {
  font-family: "Familjen Grotesk", sans-serif; font-weight: 700;
  font-size: clamp(72px, 22vw, 150px); line-height: 1; letter-spacing: -.04em;
  font-variant-numeric: tabular-nums;
  transition: color .3s ease;
}
.zz-szene-zahl-ziel { color: var(--balance); }
.zz-szene-einheit { font-size: .38em; opacity: .55; margin-left: .08em; }

.zz-band { position: relative; height: 84px; margin: 26px 0 30px; }
.zz-band-linie {
  position: absolute; top: 40px; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, var(--balance) 0%, var(--balance) 20%,
    rgba(247,244,250,.28) 36%, rgba(247,244,250,.28) 62%, var(--signal) 100%);
}
.zz-band-zone {
  position: absolute; top: 24px; left: 4%; width: 18%; height: 34px;
  background: rgba(76,160,123,.16);
  border-left: 1px solid rgba(76,160,123,.6);
  border-right: 1px solid rgba(76,160,123,.6);
}
.zz-band-marke {
  position: absolute; top: 16px; width: 2px; height: 50px; background: var(--creme);
  will-change: left;
}
.zz-band-marke::after {
  content: ""; position: absolute; bottom: -6px; left: -5px;
  width: 12px; height: 12px; border-radius: 50%; background: var(--gold);
  border: 2px solid var(--aubergine);
}
.zz-band-achse {
  position: absolute; top: 56px; left: 0; right: 0; display: flex;
  justify-content: space-between; font-family: "IBM Plex Mono", monospace;
  font-size: 11px; opacity: .55;
}
.zz-band-ziel { color: var(--balance); opacity: 1; }

.zz-kapitel-buehne { position: relative; min-height: 190px; }
.zz-kapitel {
  position: absolute; inset: 0;
  opacity: 0; transform: translateY(22px);
  transition: opacity .45s ease, transform .45s var(--ease);
  pointer-events: none;
}
.zz-kapitel-an { opacity: 1; transform: none; pointer-events: auto; }
.zz-kapitel-alle .zz-kapitel { position: static; margin-bottom: 26px; }
.zz-nr { font-family: "IBM Plex Mono", monospace; font-size: 11.5px; color: var(--gold); letter-spacing: .1em; }
.zz-kapitel h3 { font-size: 24px; margin: 7px 0 9px; }
.zz-kapitel p { margin: 0; opacity: .82; max-width: 44ch; }

.zz-szene-punkte { display: flex; gap: 8px; margin-top: 26px; }
.zz-punktchen {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--linie-hell); transition: background .3s ease, transform .3s ease;
}
.zz-punktchen-an { background: var(--gold); transform: scale(1.3); }

/* ---- Reveal */
[data-reveal] {
  opacity: 0; transform: translateY(26px);
  transition: opacity .7s ease, transform .7s var(--ease);
}
[data-reveal].zz-sichtbar { opacity: 1; transform: none; }
.zz-karten article:nth-child(2) { transition-delay: .08s; }
.zz-karten article:nth-child(3) { transition-delay: .16s; }
.zz-karten article:nth-child(4) { transition-delay: .24s; }
.zz-zahlen div:nth-child(2) { transition-delay: .1s; }
.zz-zahlen div:nth-child(3) { transition-delay: .2s; }

/* ---- Text-Fill */
.zz-textfill { margin: 0 0 40px; }
.zz-wort { color: rgba(74,52,35,.26); transition: color .35s ease; }
.zz-wort-an { color: var(--aubergine); }

/* ---- Parallax */
.zz-parallax { overflow: hidden; border-radius: 4px; }
.zz-parallax-inner { will-change: transform; transform: scale(1.12); }
.zz-bild { display: block; width: 100%; }
img.zz-bild { aspect-ratio: 3 / 2; object-fit: cover; }
.zz-parallax-hoch img.zz-bild { aspect-ratio: 3 / 4; }
.zz-bild-platzhalter {
  aspect-ratio: 3 / 2;
  background:
    radial-gradient(120% 90% at 78% 18%, rgba(190,122,61,.34) 0%, transparent 58%),
    radial-gradient(100% 80% at 12% 88%, rgba(76,160,123,.3) 0%, transparent 60%),
    var(--aubergine);
}
.zz-parallax-hoch .zz-bild-platzhalter { aspect-ratio: 3 / 4; }

/* ---- Zahlen */
.zz-zahlen { background: var(--tief); color: var(--creme); padding: 40px 22px; display: grid; gap: 26px; }
.zz-zahlen div { border-top: 1px solid var(--linie-hell); padding-top: 14px; }
.zz-zahl {
  display: block; font-family: "Familjen Grotesk", sans-serif; font-weight: 700;
  font-size: 38px; letter-spacing: -.03em; line-height: 1; font-variant-numeric: tabular-nums;
}
.zz-zahl-label { display: block; font-size: 14.5px; opacity: .72; margin-top: 7px; max-width: 30ch; }

/* ---- Warum */
.zz-warum { padding: 60px 22px 48px; max-width: 780px; margin: 0 auto; }
.zz-h2 { font-size: clamp(28px, 7.5vw, 44px); line-height: 1.06; margin-bottom: 16px; }
.zz-abschnitt-lead { font-size: 17.5px; max-width: 46ch; }
.zz-termin .zz-abschnitt-lead { opacity: .78; margin: 0 0 40px; }
.zz-karten { display: grid; gap: 26px; }
.zz-karten article { border-top: 2px solid var(--aubergine); padding-top: 15px; }
.zz-kartenr { font-family: "IBM Plex Mono", monospace; font-size: 11.5px; color: var(--gold); letter-spacing: .1em; }
.zz-karten h3 { font-size: 20px; margin: 7px 0 8px; }
.zz-karten p { margin: 0; opacity: .82; }
.zz-karten sup { font-size: 10px; color: var(--gold); }
.zz-warum-bild { margin: 40px 0 26px; }
.zz-fussnoten { font-size: 12.5px; opacity: .6; line-height: 1.5; margin: 0; }
.zz-fussnoten sup { color: var(--gold); }

/* ---- Kit */
.zz-kit { border-top: 1px solid var(--linie); }
.zz-kit .zz-parallax { border-radius: 0; }
.zz-kit-text { max-width: 780px; margin: 0 auto; padding: 44px 22px 56px; }
.zz-kit-text p { opacity: .8; max-width: 48ch; margin: 14px 0 0; }

/* ---- Nach dem Test */
.zz-danach { border-top: 1px solid var(--linie); padding: 56px 22px 52px; }
.zz-danach-inner { max-width: 780px; margin: 0 auto; }
.zz-danach-schritte { display: grid; gap: 26px; }
.zz-danach-schritte article { border-top: 2px solid var(--aubergine); padding-top: 15px; }
.zz-danach-schritte h3 { font-size: 20px; margin: 7px 0 8px; }
.zz-danach-schritte p { margin: 0; opacity: .82; font-size: 15.5px; }
.zz-danach-schritte article:nth-child(2) { transition-delay: .08s; }
.zz-danach-schritte article:nth-child(3) { transition-delay: .16s; }
.zz-danach-schritte article:nth-child(4) { transition-delay: .24s; }
.zz-marke-zeile {
  margin: 36px 0 0; opacity: .8; max-width: 62ch; font-size: 15px;
  line-height: 1.65; border-left: 2px solid var(--gold); padding-left: 16px;
}

/* ---- Termin */
.zz-termin { background: var(--tief); color: var(--creme); padding: 56px 22px 68px; }
.zz-termin-kopf { max-width: 620px; margin: 0 auto 30px; }
.zz-hinweis { max-width: 620px; margin: 0 auto; opacity: .75; }

.zz-tage { display: flex; gap: 10px; overflow-x: auto; padding-bottom: 12px;
  max-width: 620px; margin: 0 auto; scrollbar-width: none; }
.zz-tage::-webkit-scrollbar { display: none; }
.zz-tag {
  flex: none; font: inherit; cursor: pointer; text-align: left;
  background: transparent; color: inherit; border: 1px solid var(--linie-hell);
  border-radius: 10px; padding: 11px 15px; min-width: 96px;
  transition: background .18s ease, border-color .18s ease;
}
.zz-tag-aktiv { background: var(--creme); color: var(--aubergine); border-color: var(--creme); }
.zz-tag-wochentag { display: block; font-family: "IBM Plex Mono", monospace;
  font-size: 11px; letter-spacing: .12em; text-transform: uppercase; opacity: .7; }
.zz-tag-datum { display: block; font-family: "Familjen Grotesk", sans-serif;
  font-weight: 600; font-size: 17px; margin-top: 3px; }
.zz-tag-anzahl { display: block; font-size: 12px; opacity: .65; margin-top: 2px; }

.zz-slots { display: flex; flex-wrap: wrap; gap: 10px; max-width: 620px; margin: 18px auto 0; }
.zz-slot {
  font: inherit; font-family: "IBM Plex Mono", monospace; font-size: 15px;
  cursor: pointer; background: transparent; color: inherit;
  border: 1px solid var(--linie-hell); border-radius: 999px; padding: 11px 20px;
  transition: background .18s ease, border-color .18s ease;
}
.zz-slot:hover { border-color: var(--gold); }
.zz-slot-aktiv { background: var(--gold); color: var(--tief); border-color: var(--gold); }
.zz-slots[data-fehler="true"] { outline: 1px dashed var(--signal); outline-offset: 8px; }

.zz-formular { max-width: 620px; margin: 34px auto 0; }
.zz-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 520px) { .zz-grid { grid-template-columns: 1fr; } }

.zz-feld { display: flex; flex-direction: column; margin-bottom: 18px; }
.zz-feld label {
  font-family: "IBM Plex Mono", monospace; font-size: 10.5px;
  letter-spacing: .12em; text-transform: uppercase; opacity: .62; margin-bottom: 7px;
}
.zz-feld input, .zz-feld textarea {
  font-family: inherit; font-size: 17px; color: inherit; background: transparent;
  border: none; border-bottom: 1.5px solid var(--linie-hell);
  padding: 9px 2px; border-radius: 0; width: 100%;
}
.zz-feld textarea { border: 1.5px solid var(--linie-hell); padding: 10px; resize: vertical; }
.zz-feld input:focus, .zz-feld textarea:focus { border-color: var(--gold); }
.zz-feld[data-fehler="true"] input { border-color: var(--signal); }
.zz-fehler { color: #F0A090; font-size: 14px; margin-top: 6px; display: block; }

.zz-consent { display: flex; gap: 11px; align-items: flex-start; margin-top: 12px; }
.zz-consent input { margin-top: 5px; width: 17px; height: 17px; accent-color: var(--gold); flex: none; }
.zz-consent label { font-size: 14px; opacity: .82; line-height: 1.45; }
.zz-hp { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
.zz-serverfehler { color: #F0A090; font-size: 15px; margin: 18px 0 0;
  border-left: 2px solid var(--signal); padding-left: 12px; }

.zz-bestaetigung { max-width: 620px; margin: 0 auto; text-align: center; padding: 20px 0; }
.zz-haken {
  display: inline-flex; align-items: center; justify-content: center;
  width: 54px; height: 54px; border-radius: 50%;
  background: var(--balance); color: var(--tief); font-size: 27px; margin-bottom: 18px;
}
.zz-bestaetigung h2 { font-size: 32px; margin-bottom: 12px; }
.zz-bestaetigung-termin { font-family: "IBM Plex Mono", monospace; font-size: 18px; color: var(--gold); margin: 0 0 4px; }
.zz-bestaetigung-ort { opacity: .7; font-size: 15px; margin: 0 0 18px; }
.zz-bestaetigung p { opacity: .82; }

/* ---- Beraterin */
.zz-beraterin { display: flex; align-items: center; gap: 14px; margin-top: -22px; margin-bottom: 34px; }
.zz-beraterin-foto {
  width: 58px; height: 58px; border-radius: 50%; object-fit: cover;
  border: 2px solid var(--gold); flex: none;
}
.zz-beraterin-name { font-family: "Familjen Grotesk", sans-serif; font-weight: 700; font-size: 16px; margin: 0; }
.zz-beraterin-rolle { font-size: 12.5px; opacity: .65; margin: 2px 0 0; }

/* ---- FAQ */
.zz-faqs { max-width: 720px; margin: 0 auto; padding: 52px 22px 40px; }
.zz-faqs .zz-h2 { margin-bottom: 24px; }
.zz-faq { border-top: 1px solid var(--linie); }
.zz-faq button {
  width: 100%; display: flex; justify-content: space-between; align-items: center;
  gap: 14px; text-align: left; font: inherit;
  font-family: "Familjen Grotesk", sans-serif; font-weight: 600; font-size: 17px;
  background: none; border: none; color: inherit; padding: 17px 0; cursor: pointer;
}
.zz-faq-zeichen { font-family: "IBM Plex Mono", monospace; color: var(--gold); font-size: 20px; flex: none; }
.zz-faq p { margin: 0 0 18px; opacity: .8; max-width: 52ch; }

/* ---- Fuß */
.zz-fuss { border-top: 1px solid var(--linie); padding: 26px 22px 96px;
  font-size: 13px; opacity: .68; display: grid; gap: 12px; }
.zz-disclaimer { margin: 0; line-height: 1.5; max-width: 62ch; }
.zz-fuss p { margin: 0; }

/* ---- Sticky CTA */
.zz-sticky {
  position: fixed; left: 16px; right: 16px; bottom: 16px; z-index: 55;
  background: var(--gold); color: var(--tief); text-align: center;
  font-family: "Familjen Grotesk", sans-serif; font-weight: 600; font-size: 16px;
  padding: 15px; border-radius: 999px; text-decoration: none;
  box-shadow: 0 8px 26px rgba(27,2,48,.3);
  transform: translateY(120%); opacity: 0;
  transition: transform .35s var(--ease), opacity .28s ease;
  pointer-events: none;
}
.zz-sticky-an { transform: none; opacity: 1; pointer-events: auto; }

/* ---- Ab Tablet */
@media (min-width: 860px) {
  .zz-hero { padding: 76px 40px 88px; }
  .zz-hero > .zz-hero-inhalt { margin: 0; }
  .zz-hero { display: grid; grid-template-columns: 1.15fr .85fr; gap: 40px; align-items: center; }
  .zz-hero-bild { display: block; }
  .zz-szene-sticky { padding: 40px; }
  .zz-zahlen { grid-template-columns: repeat(3, 1fr); gap: 34px; padding: 46px 40px; }
  .zz-warum, .zz-faqs { padding-left: 40px; padding-right: 40px; }
  .zz-danach { padding: 64px 40px 60px; }
  .zz-danach-schritte { grid-template-columns: repeat(4, 1fr); gap: 24px; }
  .zz-karten { grid-template-columns: 1fr 1fr; gap: 30px 34px; }
  .zz-kit { display: grid; grid-template-columns: .9fr 1.1fr; align-items: center; }
  .zz-kit-text { padding: 60px 40px; }
  .zz-termin { padding: 70px 40px 84px; }
  .zz-sticky { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .zz-root *, .zz-root *::before, .zz-root *::after {
    transition-duration: .01ms !important; animation-duration: .01ms !important;
  }
  .zz-scrollwink { display: none; }
  .zz-sticky { transition: none; }
  .zz-wort { color: var(--aubergine); }
}
`;
